import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, PlayCircle, Sparkles, BadgeIndianRupee, Headphones } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import SEO, { faqSchema } from "../components/SEO";
import Reveal, { MaskLines } from "../components/Reveal";
import BookingWidget from "../components/BookingWidget";
import Marquee from "../components/Marquee";
import CtaBand from "../components/CtaBand";
import FaqAccordion from "../components/FaqAccordion";
import GoogleReviews from "../components/GoogleReviews";
import { SectionHead, ServiceCard, FleetCard, RouteCard, CityCard, TourCard, DynIcon } from "../components/Cards";
import { SITE, waLink, img, IMAGES } from "../data/site";
import { TRUST, SERVICES, FLEET, ROUTES, DESTINATIONS, TOURS, FAQS, ALL_IMAGES } from "../data/content";
import { useLang, TRUST_HI } from "../i18n";

const HERO_IMGS = [
  { src: IMAGES.heroJodhpur, alt: "Blue City of Jodhpur with Mehrangarh Fort — taxi service in Jodhpur by Blue City Taxi" },
  { src: IMAGES.mehrangarh, alt: "Mehrangarh Fort above the blue houses of Jodhpur, Rajasthan" },
  { src: IMAGES.jaipur, alt: "Hawa Mahal, Jaipur — Rajasthan tour by taxi" },
  { src: IMAGES.udaipur, alt: "Lake Pichola and palaces of Udaipur, Rajasthan" },
  { src: ALL_IMAGES.desertSunset, alt: "Sunset over Thar desert dunes near Jaisalmer, Rajasthan" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const { lang, t } = useLang();
  const trust = lang === "hi" ? TRUST_HI : TRUST;
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_IMGS.length), 5200);
    return () => clearInterval(id);
  }, []);

  const chips = [
    [Sparkles, t("chip1")],
    [ShieldCheck, t("chip2")],
    [BadgeIndianRupee, t("chip3")],
    [Headphones, t("chip4")],
  ];

  return (
    <>
      <SEO
        title="Taxi Service in Jodhpur & Across Rajasthan"
        description="Book clean, comfortable and reliable taxis in Jodhpur & all Rajasthan — local travel, one-way trips, airport transfers, outstation journeys and Rajasthan sightseeing. Call 094602 05326."
        path="/"
        image={IMAGES.heroJodhpur}
        schemas={[faqSchema(FAQS.slice(0, 6))]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative bg-navy overflow-hidden grain" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          {/* Admin: replace the slideshow below with a muted, looping Rajasthan travel video by adding a <video> tag with the same classes */}
          {HERO_IMGS.map((h, i) => (
            <img
              key={h.src}
              src={img(h.src, 1920)}
              alt={h.alt}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "auto"}
              aria-hidden={i !== slide}
              className={`absolute inset-0 w-full h-[115%] object-cover kenburns transition-opacity duration-[1600ms] ease-out ${
                i === slide ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/60 to-navy/25" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 sm:pt-28 lg:pt-32 sm:pb-28">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 backdrop-blur px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-gold shadow-sm"
            data-testid="hero-badge"
          >
            <ShieldCheck size={13} /> {t("heroBadge")}
          </motion.div>
          <h1 className="mt-6 font-serif font-bold tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl leading-[1.08] drop-shadow-md">
            <MaskLines
              lines={[
                <>{t("heroL1")}</>,
                <>{t("heroL2a")}<span className="text-gold italic">{t("heroL2b")}</span>{t("heroL2c")}</>,
                <>{t("heroL3")}</>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-6 max-w-xl text-white/85 text-sm sm:text-lg leading-relaxed"
          >
            {t("heroSub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <a href="#book" data-testid="hero-book-btn" className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-heading font-extrabold px-8 py-4 text-base tracking-wide shadow-lg shadow-gold/25 hover:bg-white hover:-translate-y-0.5 transition-all duration-300">
              {t("bookTaxi")}
            </a>
            <a href={`tel:${SITE.phoneTel}`} data-testid="hero-call-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 backdrop-blur text-white font-bold px-8 py-4 text-base hover:bg-white/20 transition-all duration-300">
              <Phone size={16} /> {t("stickyCall")} {SITE.phoneDisplay}
            </a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-wa text-white font-bold px-8 py-4 text-base hover:brightness-95 hover:-translate-y-0.5 transition-all duration-300">
              <FaWhatsapp size={17} /> WhatsApp
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-9 flex flex-wrap gap-2.5"
            data-testid="hero-trust-chips"
          >
            {chips.map(([I, label]) => (
              <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-md px-4 py-2 text-[11px] sm:text-xs font-bold text-white/90">
                <I size={13} className="text-gold" /> {label}
              </span>
            ))}
          </motion.div>
          <div className="mt-10 flex gap-2" data-testid="hero-slide-dots">
            {HERO_IMGS.map((h, i) => (
              <button
                key={h.src}
                onClick={() => setSlide(i)}
                aria-label={`Show ${h.alt}`}
                data-testid={`hero-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === slide ? "w-8 bg-gold" : "w-3 bg-white/35 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING WIDGET — dedicated section below hero */}
      <section id="book" className="relative bg-navy border-y border-gold/25 py-14 sm:py-16 grain" data-testid="booking-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-8">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white tracking-tight">{t("wSecTitle")}</h2>
            <p className="mt-3 text-sm text-white/60 max-w-md mx-auto">{t("wSecSub")}</p>
          </Reveal>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <BookingWidget />
          </motion.div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-6" data-testid="trust-section">
        <SectionHead eyebrow={t("trustEyebrow")} title={t("trustTitle")} />
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {trust.map((tr, i) => (
            <Reveal key={tr.title} delay={(i % 3) * 0.08}>
              <div className="group bg-white rounded-2xl border border-slate-200/80 p-6 h-full hover:shadow-lg hover:border-royal/25 transition-all">
                <span className="w-11 h-11 rounded-xl bg-royal-50 flex items-center justify-center text-royal group-hover:bg-royal group-hover:text-gold transition-colors">
                  <DynIcon name={tr.icon} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-navy">{tr.title}</h3>
                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{tr.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* SERVICES PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <SectionHead eyebrow={t("svcEyebrow")} title={t("svcTitle")} text={t("svcText")} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.slice(0, 6).map((s, i) => <ServiceCard key={s.slug} s={s} i={i} />)}
        </div>
        <Reveal className="mt-10 text-center">
          <Link to="/services" data-testid="home-all-services-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-royal text-royal font-bold text-sm px-7 py-3 hover:bg-royal hover:text-white transition-colors">
            {t("svcAll")}
          </Link>
        </Reveal>
      </section>

      {/* CINEMATIC RAJASTHAN */}
      <section className="relative h-[70vh] min-h-[440px] overflow-hidden grain" data-testid="explore-rajasthan-section">
        <img
          src={img(ALL_IMAGES.desertSunset, 1920)}
          alt="Sunset over the Thar desert — explore Rajasthan with Blue City Taxi"
          loading="lazy"
          className="absolute inset-0 w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/40 to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-6 flex items-center">
          <Reveal className="max-w-xl">
            <span className="inline-flex items-center gap-2 text-gold text-xs font-bold uppercase tracking-[0.25em]"><PlayCircle size={15} /> {t("cineEyebrow")}</span>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight">
              {t("cineTitle")}
            </h2>
            <p className="mt-4 text-white/80 text-sm sm:text-base leading-relaxed">{t("cineText")}</p>
            <Link to="/tour-packages" data-testid="explore-tours-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-7 py-3.5 text-sm hover:bg-white transition-colors">
              {t("cineBtn")}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FLEET PREVIEW */}
      <section className="bg-royal-50/60">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <SectionHead eyebrow={t("fleetEyebrow")} title={t("fleetTitle")} text={t("fleetText")} />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FLEET.slice(0, 3).map((v, i) => <FleetCard key={v.id} v={v} i={i} />)}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/fleet" data-testid="home-fleet-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-royal text-royal font-bold text-sm px-7 py-3 hover:bg-royal hover:text-white transition-colors">
              {t("fleetAll")}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ONE WAY PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <SectionHead eyebrow={t("owEyebrow")} title={t("owTitle")} text={t("owText")} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ROUTES.slice(0, 4).map((r, i) => <RouteCard key={r.to} r={r} i={i} />)}
        </div>
        <Reveal className="mt-10 text-center">
          <Link to="/one-way-taxi" data-testid="home-oneway-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-royal text-royal font-bold text-sm px-7 py-3 hover:bg-royal hover:text-white transition-colors">
            {t("owAll")}
          </Link>
        </Reveal>
      </section>

      {/* DESTINATIONS */}
      <section className="bg-navy grain relative">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <SectionHead eyebrow={t("destEyebrow")} title={t("destTitle")} />
          <p className="text-center text-white/60 text-sm max-w-xl mx-auto -mt-4">{t("destSub")}</p>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {DESTINATIONS.slice(0, 8).map((d, i) => <CityCard key={d.name} d={d} i={i} />)}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/all-rajasthan-taxi" data-testid="home-all-rajasthan-btn" className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold text-sm px-7 py-3 hover:bg-white transition-colors">
              {t("destAll")}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TOURS PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <SectionHead eyebrow={t("tourEyebrow")} title={t("tourTitle")} text={t("tourText")} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TOURS.slice(0, 3).map((tour, i) => <TourCard key={tour.name} t={tour} i={i} />)}
        </div>
        <Reveal className="mt-10 text-center">
          <Link to="/tour-packages" data-testid="home-tours-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-royal text-royal font-bold text-sm px-7 py-3 hover:bg-royal hover:text-white transition-colors">
            {t("tourAll")}
          </Link>
        </Reveal>
      </section>

      {/* REVIEWS */}
      <GoogleReviews />

      {/* FAQ PREVIEW */}
      <section className="max-w-3xl mx-auto px-6 py-20">
        <SectionHead eyebrow={t("faqEyebrow")} title={t("faqTitle")} />
        <div className="mt-8">
          <FaqAccordion faqs={FAQS.slice(0, 5)} idPrefix="home-faq" />
        </div>
        <Reveal className="mt-8 text-center">
          <Link to="/faq" data-testid="home-faq-btn" className="text-sm font-bold text-royal hover:text-gold-dark transition-colors">
            {t("faqAll")}
          </Link>
        </Reveal>
      </section>

      <CtaBand />
    </>
  );
}
